import theme from '../theme/theme';
import type {
  SimulationResultInterface,
  WarningsInterface,
} from '../store/simulation/types';
import { isEmpty, uniq } from 'lodash';
import type { UnitInterface } from 'store/units/types';
import type { MetricType } from 'store/settings/types';
import { convertUnitsInText } from './conversions';

export type Severity = 'minor' | 'moderate' | 'major';

export const getSeverityTitle = (s: Severity) => {
  if (s === 'major') {
    return 'MAJOR';
  } else if (s === 'moderate') {
    return 'MODERATE';
  } else {
    return 'MINOR';
  }
};

export const getSeverityColor = (s: Severity) => {
  if (s === 'major') {
    return theme.palette.warnings.major;
  } else if (s === 'moderate') {
    return theme.palette.warnings.moderate;
  } else {
    return theme.palette.warnings.minor;
  }
};

export type GroupedWarnings = {
  severity: Severity;
  messages: string[];
};

const getWarningMessages = (
  warnings: WarningsInterface,
  severity: keyof Pick<WarningsInterface, 'MAJOR' | 'MODERATE' | 'MINOR'>,
) => warnings[severity] ?? [];

// Returns an array of warnings grouped by severity sorted by highest severity
// Will not include severities with zero messages
export const groupWarningsBySeverity = (
  warnings: WarningsInterface | undefined,
  metricType?: MetricType,
): GroupedWarnings[] => {
  if (!warnings || isEmpty(warnings)) {
    return [];
  }

  // Helper to convert messages if metricType is provided
  const processMessages = (messages: { message: string }[]) => {
    const uniqueMessages = uniq(messages.map((warning) => warning.message));
    if (metricType) {
      return uniqueMessages.map((msg) => convertUnitsInText(msg, metricType));
    }
    return uniqueMessages;
  };

  return [
    {
      severity: 'major',
      messages: processMessages(getWarningMessages(warnings, 'MAJOR')),
    } as GroupedWarnings,
    {
      severity: 'moderate',
      messages: processMessages(getWarningMessages(warnings, 'MODERATE')),
    } as GroupedWarnings,
    {
      severity: 'minor',
      messages: processMessages(getWarningMessages(warnings, 'MINOR')),
    } as GroupedWarnings,
  ].filter((warnings) => warnings.messages.length > 0);
};

export const totalWarningsCount = (
  groupedWarnings: GroupedWarnings[],
): number => {
  return groupedWarnings.reduce((acc, next) => acc + next.messages.length, 0);
};

export const hasDesignAnyMajorWarnings = (
  units: UnitInterface[],
  simulationResults: { [unitKey: string]: SimulationResultInterface },
) => {
  return units.some((u) => {
    const majorCount = simulationResults[u.key]?.warnings?.MAJOR?.length ?? 0;
    return majorCount > 0;
  });
};
